/**
 * UI component factory for Supernal TTS Widget
 */

import { formatVoiceName } from '../core/text-processor';
import { AnalyticsManager } from '../core/analytics';

export class UIComponentFactory {
  private analyticsManager: AnalyticsManager;

  constructor(analyticsManager: AnalyticsManager) {
    this.analyticsManager = analyticsManager;
  }

  /**
   * Create unified logo circle (single circle with ~+)
   */
  public createUnifiedCircle(): HTMLElement {
    const circle = document.createElement('div');
    circle.className = 'supernal-tts-unified-circle';
    circle.textContent = '~+';
    circle.setAttribute('aria-label', 'Text-to-speech player');
    return circle;
  }

  /**
   * Create expandable + badge (branding link)
   */
  public createExpandableBadge(): HTMLAnchorElement {
    const badge = document.createElement('a');
    badge.className = 'supernal-tts-logo-right';
    badge.href = 'https://tts.supernal.ai';
    badge.target = '_blank';
    badge.rel = 'noopener noreferrer';
    badge.setAttribute('aria-label', 'Learn more about Supernal TTS');

    const icon = document.createElement('span');
    icon.className = 'supernal-tts-badge-icon';
    icon.textContent = '+';

    const label = document.createElement('span');
    label.className = 'supernal-tts-badge-label';
    label.textContent = 'View Site';

    badge.appendChild(icon);
    badge.appendChild(label);

    // Track click-through events
    badge.addEventListener('click', () => {
      this.analyticsManager.trackBrandingClick(badge.href);
    });

    return badge;
  }

  /**
   * Create split circles (~ | ▶ | controls... | +)
   */
  public createSplitCircles(playButton: HTMLButtonElement, voiceDropdown?: HTMLElement | null, speedDropdown?: HTMLElement | null): HTMLElement {
    const container = document.createElement('div');
    container.className = 'supernal-tts-split-circles';

    // Left circle: ~ (also acts as close button when expanded)
    const tildeCircle = document.createElement('div');
    tildeCircle.className = 'supernal-tts-circle supernal-tts-tilde-circle';
    tildeCircle.textContent = '~';
    tildeCircle.setAttribute('aria-label', 'Supernal TTS');
    tildeCircle.style.cursor = 'pointer';

    // Center: play button (already created)
    playButton.classList.add('supernal-tts-circle', 'supernal-tts-play-circle');

    // Right circle: +
    const plusCircle = document.createElement('a');
    plusCircle.className = 'supernal-tts-circle supernal-tts-plus-circle';
    plusCircle.textContent = '+';
    plusCircle.href = 'https://www.tts.supernal.ai';
    plusCircle.target = '_blank';
    plusCircle.rel = 'noopener noreferrer';
    plusCircle.setAttribute('aria-label', 'Learn more');

    // Track whether controls are revealed and if widget has been interacted with
    let controlsRevealed = false;
    let hasInteracted = false; // Once true, widget stays expanded
    let hoverTimeout: number | null = null;

    // Add glow effect if controls are present, and add class to indicate which controls
    if (voiceDropdown || speedDropdown) {
      plusCircle.classList.add('has-controls');

      // Add class to indicate which controls are present for proper positioning
      if (voiceDropdown && speedDropdown) {
        plusCircle.setAttribute('data-controls', 'both-controls');
      } else if (voiceDropdown) {
        plusCircle.setAttribute('data-controls', 'voice-only');
      } else if (speedDropdown) {
        plusCircle.setAttribute('data-controls', 'speed-only');
      }
    }

    // When hovering +, reveal controls and change to arrow
    // Use flag to prevent accidental trigger when circles appear under cursor
    let isActuallyHovering = false;

    plusCircle.addEventListener('mouseenter', () => {
      isActuallyHovering = true;

      // Only change if user moves mouse (not just circles appearing)
      hoverTimeout = window.setTimeout(() => {
        if (isActuallyHovering && plusCircle.matches(':hover')) {
          // Show controls if they exist
          if (voiceDropdown || speedDropdown) {
            plusCircle.textContent = '→';
            controlsRevealed = true;
            plusCircle.classList.add('controls-revealed');

            // Add the specific control class for proper positioning
            const controlType = plusCircle.getAttribute('data-controls');
            if (controlType) {
              plusCircle.classList.add(controlType);
            }

            if (voiceDropdown) voiceDropdown.classList.add('controls-visible');
            if (speedDropdown) speedDropdown.classList.add('controls-visible');
          }
        }
      }, 150);
    });

    plusCircle.addEventListener('mouseleave', () => {
      isActuallyHovering = false;
      // Clear timeout if leaving before it fires
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
    });

    // Function to permanently expand controls (after any interaction)
    const expandControlsPermanently = () => {
      if (!hasInteracted && (voiceDropdown || speedDropdown)) {
        hasInteracted = true;
        controlsRevealed = true;
        plusCircle.textContent = '→';
        plusCircle.classList.add('controls-revealed');

        // Find the parent modular widget container and mark it as permanently expanded
        const modularWidget = container.closest('.supernal-tts-modular-widget');
        if (modularWidget) {
          modularWidget.classList.add('permanently-expanded');
        }

        // Add the specific control class for proper positioning
        const controlType = plusCircle.getAttribute('data-controls');
        if (controlType) {
          plusCircle.classList.add(controlType);
        }

        if (voiceDropdown) voiceDropdown.classList.add('controls-visible');
        if (speedDropdown) speedDropdown.classList.add('controls-visible');
      }
    };

    // Store reference to reset function for later (after container is appended to DOM)
    const resetControls = () => {
      // Don't reset if user has interacted - keep it expanded permanently
      if (hasInteracted) {
        return;
      }

      if (controlsRevealed) {
        controlsRevealed = false;
        plusCircle.textContent = '+';
        plusCircle.classList.remove('controls-revealed');

        // Remove the specific control class
        const controlType = plusCircle.getAttribute('data-controls');
        if (controlType) {
          plusCircle.classList.remove(controlType);
        }

        if (voiceDropdown) voiceDropdown.classList.remove('controls-visible');
        if (speedDropdown) speedDropdown.classList.remove('controls-visible');
      }
    };

    // Function to collapse widget back to initial state
    const collapseWidget = () => {
      hasInteracted = false;
      controlsRevealed = false;
      plusCircle.textContent = '+';
      plusCircle.classList.remove('controls-revealed');

      // Remove the specific control class
      const controlType = plusCircle.getAttribute('data-controls');
      if (controlType) {
        plusCircle.classList.remove(controlType);
      }

      if (voiceDropdown) voiceDropdown.classList.remove('controls-visible');
      if (speedDropdown) speedDropdown.classList.remove('controls-visible');

      // Find the parent modular widget container and remove permanent expansion
      const modularWidget = container.closest('.supernal-tts-modular-widget');
      if (modularWidget) {
        modularWidget.classList.remove('permanently-expanded');
      }
    };

    // Tilde circle click to collapse
    tildeCircle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      collapseWidget();
    });

    // Store functions on container so they can be used after DOM insertion
    (container as any)._resetControls = resetControls;
    (container as any)._expandControlsPermanently = expandControlsPermanently;
    (container as any)._collapseWidget = collapseWidget;

    // Track click-through
    plusCircle.addEventListener('click', () => {
      this.analyticsManager.trackBrandingClick(plusCircle.href);
    });

    // Build order: ~ | ▶ | voice | speed | +
    container.appendChild(tildeCircle);
    container.appendChild(playButton);

    // Add optional controls between play and +
    if (voiceDropdown) {
      container.appendChild(voiceDropdown);
    }
    if (speedDropdown) {
      container.appendChild(speedDropdown);
    }

    container.appendChild(plusCircle);

    return container;
  }

  /**
   * Create play button
   */
  public createCircularPlayButton(): HTMLButtonElement {
    const button = document.createElement('button');
    button.setAttribute('aria-label', 'Play text-to-speech');

    const icon = document.createElement('span');
    icon.className = 'supernal-tts-play-icon-circle';
    icon.textContent = '▶';

    button.appendChild(icon);
    return button;
  }

  /**
   * Create voice dropdown with person icon button
   */
  public createVoiceDropdown(voices: string[], selectedVoice: string): HTMLElement {
    const container = document.createElement('div');
    container.className = 'supernal-tts-voice-control';

    // Person icon toggle button (no background)
    const toggleButton = document.createElement('button');
    toggleButton.className = 'supernal-tts-voice-toggle';
    toggleButton.setAttribute('aria-label', 'Select voice');
    toggleButton.setAttribute('aria-expanded', 'false');
    toggleButton.innerHTML = `
      <svg class="supernal-tts-voice-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
      </svg>
    `;

    // Dropdown menu (hidden by default)
    const dropdown = document.createElement('div');
    dropdown.className = 'supernal-tts-voice-dropdown hidden';
    dropdown.setAttribute('role', 'menu');

    voices.forEach(voice => {
      const option = document.createElement('button');
      option.className = 'supernal-tts-voice-option';
      option.textContent = formatVoiceName(voice);
      option.dataset.voice = voice;
      option.setAttribute('role', 'menuitem');

      if (voice === selectedVoice) {
        option.classList.add('active');
      }

      option.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();

        // Update active state
        dropdown.querySelectorAll('.supernal-tts-voice-option').forEach(opt => {
          opt.classList.remove('active');
        });
        option.classList.add('active');

        // Emit change event
        container.dispatchEvent(new CustomEvent('voice-changed', {
          detail: { voice }
        }));
      });

      dropdown.appendChild(option);
    });

    let hideTimeout: number | null = null;

    // Show dropdown on hover with smart positioning
    container.addEventListener('mouseenter', () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
      dropdown.classList.remove('hidden');
      toggleButton.setAttribute('aria-expanded', 'true');

      // Smart positioning: check if dropdown would go off-screen
      setTimeout(() => {
        const rect = dropdown.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        if (rect.bottom > viewportHeight && rect.top > rect.height) {
          // Not enough space below, position above
          dropdown.style.bottom = '100%';
          dropdown.style.top = 'auto';
        } else {
          // Default: position below
          dropdown.style.top = 'calc(100% + 4px)';
          dropdown.style.bottom = 'auto';
        }
      }, 0);
    });

    container.addEventListener('mouseleave', (e) => {
      // Delay hiding to allow moving to dropdown
      hideTimeout = window.setTimeout(() => {
        dropdown.classList.add('hidden');
        toggleButton.setAttribute('aria-expanded', 'false');
      }, 150);
    });

    // Keep dropdown open when hovering over it
    dropdown.addEventListener('mouseenter', () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    });

    dropdown.addEventListener('mouseleave', () => {
      hideTimeout = window.setTimeout(() => {
        dropdown.classList.add('hidden');
        toggleButton.setAttribute('aria-expanded', 'false');
      }, 150);
    });

    // Also allow click to toggle
    toggleButton.addEventListener('click', (e) => {
      e.stopPropagation();
      const isHidden = dropdown.classList.contains('hidden');
      dropdown.classList.toggle('hidden');
      toggleButton.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
    });

    container.appendChild(toggleButton);
    container.appendChild(dropdown);

    return container;
  }

  /**
   * Create speed dropdown with preset values (hover to reveal)
   */
  public createSpeedDropdown(selectedSpeed: number): HTMLElement {
    const container = document.createElement('div');
    container.className = 'supernal-tts-speed-control';

    // Speedometer/gauge icon button (no background)
    const button = document.createElement('button');
    button.className = 'supernal-tts-speed-toggle';
    button.setAttribute('aria-label', 'Adjust playback speed');
    button.innerHTML = `
      <svg class="supernal-tts-speed-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.38 8.57l-1.23 1.85a8 8 0 0 1-.22 7.58H5.07A8 8 0 0 1 15.58 6.85l1.85-1.23A10 10 0 0 0 3.35 19a2 2 0 0 0 1.72 1h13.85a2 2 0 0 0 1.74-1 10 10 0 0 0-.27-10.44z"/>
        <path d="M10.59 15.41a2 2 0 0 0 2.83 0l5.66-8.49-8.49 5.66a2 2 0 0 0 0 2.83z"/>
      </svg>
    `;

    // Dropdown menu (hidden by default, shows on hover)
    const dropdown = document.createElement('div');
    dropdown.className = 'supernal-tts-speed-dropdown hidden';
    dropdown.setAttribute('role', 'menu');

    // Speed range: 0.6 to 3.0 in steps of 0.2
    const speeds: number[] = [];
    for (let speed = 0.6; speed <= 3.0; speed += 0.2) {
      speeds.push(Math.round(speed * 10) / 10); // Round to 1 decimal
    }

    speeds.forEach(speed => {
      const option = document.createElement('button');
      option.className = 'supernal-tts-speed-option';
      option.textContent = `${speed.toFixed(1)}x`;
      option.dataset.speed = speed.toString();
      option.setAttribute('role', 'menuitem');

      if (Math.abs(speed - selectedSpeed) < 0.01) {
        option.classList.add('active');
      }

      option.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();

        // Update active state
        dropdown.querySelectorAll('.supernal-tts-speed-option').forEach(opt => {
          opt.classList.remove('active');
        });
        option.classList.add('active');

        // Emit change event
        container.dispatchEvent(new CustomEvent('speed-changed', {
          detail: { speed }
        }));
      });

      dropdown.appendChild(option);
    });

    let hideTimeout: number | null = null;

    // Show dropdown on hover with smart positioning
    container.addEventListener('mouseenter', () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
      dropdown.classList.remove('hidden');

      // Smart positioning: check if dropdown would go off-screen
      setTimeout(() => {
        const rect = dropdown.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        if (rect.bottom > viewportHeight && rect.top > rect.height) {
          // Not enough space below, position above
          dropdown.style.bottom = '100%';
          dropdown.style.top = 'auto';
        } else {
          // Default: position below
          dropdown.style.top = 'calc(100% + 4px)';
          dropdown.style.bottom = 'auto';
        }
      }, 0);
    });

    container.addEventListener('mouseleave', () => {
      // Delay hiding to allow moving to dropdown
      hideTimeout = window.setTimeout(() => {
        dropdown.classList.add('hidden');
      }, 150);
    });

    // Keep dropdown open when hovering over it
    dropdown.addEventListener('mouseenter', () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    });

    dropdown.addEventListener('mouseleave', () => {
      hideTimeout = window.setTimeout(() => {
        dropdown.classList.add('hidden');
      }, 150);
    });

    container.appendChild(button);
    container.appendChild(dropdown);

    return container;
  }

  /**
   * Create seekable progress bar with time display
   */
  public createProgressBar(onSeek: (audio: HTMLAudioElement, percentage: number) => void, getCurrentAudio: () => HTMLAudioElement | null): HTMLElement {
    const container = document.createElement('div');
    container.className = 'supernal-tts-progress-container';

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'supernal-tts-progress-slider';
    slider.min = '0';
    slider.max = '100';
    slider.value = '0';
    slider.step = '0.1';
    slider.setAttribute('aria-label', 'Seek audio position');

    const timeDisplay = document.createElement('div');
    timeDisplay.className = 'supernal-tts-time-display';
    timeDisplay.innerHTML = '<span class="current">0:00</span> / <span class="duration">0:00</span>';

    // Seeking functionality
    slider.addEventListener('input', (e) => {
      const currentAudio = getCurrentAudio();
      if (currentAudio) {
        const percentage = parseFloat((e.target as HTMLInputElement).value);
        onSeek(currentAudio, percentage);
      }
    });

    container.appendChild(slider);
    container.appendChild(timeDisplay);

    return container;
  }

  /**
   * Create branding badge
   */
  public createBrandingBadge(): HTMLAnchorElement {
    const badge = document.createElement('a');
    badge.className = 'supernal-badge';
    badge.href = 'https://www.tts.supernal.ai';
    badge.target = '_blank';
    badge.rel = 'noopener noreferrer';
    badge.title = 'Powered by Supernal TTS';
    badge.setAttribute('aria-label', 'Powered by Supernal TTS');

    // Use new TTS logo (~+) from brand-kit hosted on CDN
    const img = document.createElement('img');
    img.src = 'https://unpkg.com/@supernal/tts-widget@latest/dist/supernal-tts-logo.svg';
    img.alt = 'Supernal TTS';
    img.className = 'supernal-logo-img';
    img.style.display = 'block';
    img.style.width = '24px';
    img.style.height = '24px';

    // Fallback to ~+ text if image fails to load
    img.onerror = () => {
      badge.innerHTML = '~+';
      badge.style.fontSize = '16px';
      badge.style.fontWeight = '700';
      badge.style.display = 'flex';
      badge.style.color = '#0066ff';
    };

    badge.appendChild(img);

    // Track click-through events
    badge.addEventListener('click', () => {
      this.analyticsManager.trackBrandingClick(badge.href);
    });

    return badge;
  }

  /**
   * Create stop button (legacy)
   */
  public createStopButton(): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'supernal-tts-stop';
    button.innerHTML = `
      <svg class="supernal-tts-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <rect x="6" y="6" width="12" height="12"/>
      </svg>
      <span class="supernal-tts-text">Stop</span>
    `;
    button.setAttribute('aria-label', 'Stop text-to-speech');
    return button;
  }

  /**
   * Create play button (legacy)
   */
  public createPlayButton(compact: boolean = false): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'supernal-tts-play';

    if (compact) {
      // Compact mode: icon only, circular button
      button.classList.add('supernal-tts-play-compact');
      button.innerHTML = `
        <svg class="supernal-tts-icon supernal-tts-play-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z"/>
        </svg>
        <svg class="supernal-tts-icon supernal-tts-loading-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
        </svg>
      `;
    } else {
      // Full mode: icon + text
      button.innerHTML = `
        <svg class="supernal-tts-icon supernal-tts-play-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z"/>
        </svg>
        <svg class="supernal-tts-icon supernal-tts-loading-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
        </svg>
        <span class="supernal-tts-text">Listen</span>
      `;
    }

    button.setAttribute('aria-label', 'Play text-to-speech');
    return button;
  }
}
